import { useState } from 'react';

import { formatCurrency } from '../utils/formatCurrency';

/**
 * Input de dinero "inteligente": mientras se escribe, cada dígito se agrega
 * a la derecha y el campo se formatea en vivo con separador de miles y
 * ".00" de relleno (1 -> "1.00", 150 -> "150.00", 5690 -> "5,690.00"). Si el
 * usuario escribe un punto decimal, deja de auto-rellenar y los dígitos que
 * siguen pasan a ser los centavos reales (hasta 2), lo que permite editar un
 * monto existente con centavos (p. ej. reabrir un pago de $6.18).
 *
 * Toda la edición ocurre en `onKeyDown` (dígitos, punto, Backspace/Delete)
 * con `preventDefault`, así que el valor nativo del input nunca cambia por
 * tipeo; `onChangeText` queda como no-op solo para cumplir con el contrato
 * de un input controlado. Al salir del campo se formatea con `formatCurrency`
 * igual que antes. Requiere un input `type="text"` (no `number`): los
 * inputs nativos `type="number"` no admiten comas de miles.
 */
export const useCurrencyInput = (
  value: number | string | undefined | null,
  /** `isEmpty` es true cuando el usuario borró todo el campo. */
  onCommit: (numericValue: number, isEmpty: boolean) => void
) => {
  const [focused, setFocused] = useState(false);

  // Parte entera tal como se ha tecleado (sin comas, sin ceros a la
  // izquierda salvo el propio "0").
  const [digits, setDigits] = useState('');

  // true en cuanto el usuario teclea "." — a partir de ahí se deja de
  // auto-rellenar con "00" y los dígitos siguientes son centavos reales.
  const [hasDot, setHasDot] = useState(false);

  // Centavos reales tecleados (0-2 dígitos), solo relevante si hasDot.
  const [decimals, setDecimals] = useState('');

  const numeric =
    value === '' || value === undefined || value === null
      ? null
      : Number(value);

  const hasValue = numeric !== null && !Number.isNaN(numeric);

  const commit = (
    nextDigits: string,
    nextHasDot: boolean,
    nextDecimals: string
  ) => {
    const isEmpty = nextDigits === '' && !nextHasDot;

    if (isEmpty) {
      onCommit(0, true);
      return;
    }

    const intPart = nextDigits === '' ? '0' : nextDigits;

    const decPart = nextHasDot
      ? nextDecimals.padEnd(2, '0').slice(0, 2)
      : '00';

    onCommit(Number(`${intPart}.${decPart}`), false);
  };

  const liveDisplay = () => {
    if (digits === '' && !hasDot) return '';

    const intFormatted = Number(digits || '0').toLocaleString('en-US');

    return hasDot ? `${intFormatted}.${decimals}` : `${intFormatted}.00`;
  };

  const displayValue = focused
    ? liveDisplay()
    : hasValue
      ? formatCurrency(numeric)
      : '';

  const seedFromValue = () => {
    if (hasValue && numeric !== 0) {
      const [intPart, decPart] = Math.abs(numeric).toFixed(2).split('.');

      const cleanInt = String(Number(intPart));

      setDigits(cleanInt);

      if (decPart === '00') {
        setHasDot(false);
        setDecimals('');
      } else {
        setHasDot(true);
        setDecimals(decPart);
      }
    } else {
      setDigits('');
      setHasDot(false);
      setDecimals('');
    }
  };

  const onFocus = () => {
    setFocused(true);
    seedFromValue();
  };

  const onBlur = () => {
    setFocused(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    const target = e.currentTarget;

    const isFullySelected =
      target.value.length > 0 &&
      target.selectionStart === 0 &&
      target.selectionEnd === target.value.length;

    if (/^[0-9]$/.test(key)) {
      e.preventDefault();

      if (isFullySelected) {
        const nextDigits = key === '0' ? '0' : key;
        setDigits(nextDigits);
        setHasDot(false);
        setDecimals('');
        commit(nextDigits, false, '');
        return;
      }

      if (hasDot) {
        if (decimals.length < 2) {
          const nextDecimals = decimals + key;
          setDecimals(nextDecimals);
          commit(digits, true, nextDecimals);
        }
      } else {
        const nextDigits =
          digits === '0' || digits === '' ? key : digits + key;
        setDigits(nextDigits);
        commit(nextDigits, false, '');
      }
      return;
    }

    if (key === '.') {
      e.preventDefault();

      if (isFullySelected) {
        setDigits('');
        setHasDot(true);
        setDecimals('');
        commit('', true, '');
        return;
      }

      if (!hasDot) {
        setHasDot(true);
        setDecimals('');
        commit(digits, true, '');
      }
      return;
    }

    if (key === 'Backspace' || key === 'Delete') {
      e.preventDefault();

      if (isFullySelected) {
        setDigits('');
        setHasDot(false);
        setDecimals('');
        commit('', false, '');
        return;
      }

      if (hasDot) {
        if (decimals.length > 0) {
          const nextDecimals = decimals.slice(0, -1);
          setDecimals(nextDecimals);
          commit(digits, true, nextDecimals);
        } else {
          setHasDot(false);
          commit(digits, false, '');
        }
      } else {
        const nextDigits = digits.slice(0, -1);
        setDigits(nextDigits);
        commit(nextDigits, false, '');
      }
      return;
    }

    // Cualquier otra tecla (Tab, Enter, flechas...) sigue su curso normal.
  };

  // Se mantiene solo para cumplir el contrato de un input controlado: toda
  // la edición real ocurre en onKeyDown (con preventDefault), así que el
  // valor nativo nunca cambia por tipeo y esto nunca debería dispararse.
  const onChangeText = (_raw: string) => {};

  return {
    displayValue,
    onFocus,
    onBlur,
    onKeyDown,
    onChangeText,
  };
};
